import { Drawer, HueLogoTile } from '@hue/design-system';
import { Link } from '@tanstack/react-router';
import {
  Building2,
  BookOpen,
  Clock3,
  LogOut,
  MailCheck,
  Menu,
  Package,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { SessionUser } from '../lib/api.js';
import { ThemeToggle } from './theme-toggle.js';
import { ContextSwitcher } from './context-switcher.js';

/// A casca do console da plataforma: menu, cabeçalho, aviso de contexto root e o rodapé de conta.
/// Quem decide o que ocupa o miolo é a rota — a casca não guarda seção nenhuma.
export function RootConsole({
  user,
  content,
  onOpenTimeZoneSettings,
  onLogout,
}: {
  user: SessionUser;
  content: ReactNode;
  onOpenTimeZoneSettings: () => void;
  onLogout: () => Promise<void>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const identity = (
    <div className="flex items-center gap-3">
      <HueLogoTile className="size-8 rounded-lg" />
      <div>
        <strong className="block text-base font-bold tracking-[-.03em]">
          HUE<span className="text-brand-400">•</span>System
        </strong>
        <span className="block text-[10px] font-semibold uppercase tracking-[.14em] text-content-faint">
          Console global
        </span>
      </div>
    </div>
  );

  /// As áreas da plataforma. Cada uma é uma página com breadcrumb e título próprios — não são
  /// ângulos do mesmo assunto, e por isso moram no menu, não em abas dentro de uma tela. Abas
  /// ficam para o que é a mesma entidade vista de outro jeito, como Pessoas e Perfis de acesso.
  /// A lista existe porque a mesma navegação é montada duas vezes, na barra lateral e na gaveta.
  ///
  /// Comunicação aponta para `/communication` e não para uma das abas: o item precisa continuar
  /// sendo o atual nas três, e o casamento do endereço é por prefixo.
  const platform =
    user.context?.kind === 'PLATFORM'
      ? user.context
      : user.platformRole === 'ROOT'
        ? { kind: 'PLATFORM' as const, roleKeys: ['ROOT'], permissions: [], isRoot: true }
        : null;
  const can = (permission: string) =>
    platform?.isRoot === true || platform?.permissions.includes(permission);
  const sections = [
    ...(can('platform.access.read')
      ? [{ to: '/platform-access' as const, label: 'Acessos', icon: UsersRound }]
      : []),
    ...(can('platform.tenants.read')
      ? [{ to: '/companies' as const, label: 'Tenants', icon: Building2 }]
      : []),
    ...(can('platform.plans.read')
      ? [{ to: '/plans' as const, label: 'Planos', icon: Package }]
      : []),
    ...(can('platform.technical_documentation.read')
      ? [{ to: '/technical-documentation' as const, label: 'Documentação técnica', icon: BookOpen }]
      : []),
    // Comunicação, e não "Caixa de saída": o rótulo é o mesmo que o catálogo de permissões já usa,
    // e é o guarda-chuva das três abas de e-mail.
    ...(can('platform.email_messages.read') ||
    can('platform.email_templates.read') ||
    can('platform.communication_settings.read')
      ? [{ to: '/communication' as const, label: 'Comunicação', icon: MailCheck }]
      : []),
  ] as const;

  /// Os itens são links, e não botões: quem quiser abrir Planos numa aba nova, ou copiar o
  /// endereço para mandar, consegue. Botão que navega tira isso de quem usa o navegador de
  /// verdade. O item atual se anuncia pelo `aria-current` que o próprio roteador resolve — e num
  /// endereço que não é de nenhuma seção, como o 404, nenhum deles é o atual.
  const navigation = (onNavigate?: () => void) => (
    <nav aria-label="Console global" className="flex-1 px-2 py-4">
      <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[.14em] text-content-faint">
        Plataforma
      </p>
      <div className="space-y-1">
        {sections.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => onNavigate?.()}
            className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-xl border px-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
            activeProps={{
              'aria-current': 'page',
              className: 'border-brand-500/20 bg-brand-500/15 text-brand-400',
            }}
            inactiveProps={{
              className:
                'border-transparent text-content-muted hover:bg-content/5 hover:text-content',
            }}
          >
            <item.icon className="size-5 shrink-0" aria-hidden="true" />
            {item.label}
          </Link>
        ))}
      </div>
    </nav>
  );

  const account = (
    <div className="border-t border-surface-border p-3">
      <button
        type="button"
        onClick={onOpenTimeZoneSettings}
        aria-label="Configurar fuso horário"
        className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-500 text-sm font-bold text-on-brand">
          {user.firstName.slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm font-medium">
            {user.firstName} {user.lastName}
          </strong>
          <span className="block truncate text-xs text-content-muted">
            {platform?.isRoot ? 'Root global' : (platform?.roleKeys.join(', ') ?? 'Plataforma')}
          </span>
        </div>
        <Clock3 className="size-4 shrink-0 text-content-faint" />
      </button>
      <div className="mt-2">
        <ContextSwitcher />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <ThemeToggle user={user} />
        <button
          type="button"
          onClick={() => void onLogout()}
          className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-xl border border-surface-border text-xs font-medium text-content-secondary transition hover:border-danger/50 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <LogOut className="size-4" />
          Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="sticky top-0 hidden h-screen flex-col border-r border-surface-border bg-surface-card lg:flex">
        <div className="flex h-16 items-center border-b border-surface-border px-4">{identity}</div>
        {navigation()}
        {account}
      </aside>

      <Drawer open={menuOpen} onOpenChange={setMenuOpen} title="Menu do console global">
        <div className="flex h-16 shrink-0 items-center border-b border-surface-border pl-4 pr-14">
          {identity}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{navigation(() => setMenuOpen(false))}</div>
        {account}
      </Drawer>

      <div className="min-w-0">
        {/* Celular: só o menu e a identidade, como no shell do tenant. Fuso, tema e sair moram na
            gaveta, junto de quem está logado. */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-surface-border bg-surface-card px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
            aria-expanded={menuOpen}
            className="grid size-10 shrink-0 cursor-pointer place-items-center rounded-xl border border-surface-border text-content-secondary transition hover:border-brand-500/50 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>
          {identity}
        </header>

        <div className="border-b border-brand-500/20 bg-brand-500/[.06]">
          <div className="flex items-center gap-2 px-5 py-2.5 text-xs sm:px-8">
            <ShieldCheck className="size-4 text-brand-400" />
            <strong className="text-content/75">
              {platform?.isRoot ? 'Contexto root ativo.' : 'Contexto da plataforma ativo.'}
            </strong>
            <span className="hidden text-content-muted sm:inline">
              Nenhuma empresa está em atuação administrativa.
            </span>
          </div>
        </div>

        <main id="conteudo" className="hue-page px-5 py-8 sm:px-8 sm:py-10">
          {content}
        </main>
      </div>
    </div>
  );
}
